import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface TooltipProps {
    id?: string;
    message: string;
    children: React.ReactNode;
    position?: 'top' | 'bottom' | 'left' | 'right';
    maxWidth?: number;
    offset?: number;
}

const Tooltip: React.FC<TooltipProps> = ({
    id,
    message,
    children,
    position = 'right',
    maxWidth = 400,
    offset = 8,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
    const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');
    const [tooltipWidth, setTooltipWidth] = useState<number>(maxWidth);
    const [tooltipMaxHeight, setTooltipMaxHeight] = useState<number>(400);
    const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scrollRafRef = useRef<number | null>(null);
    const hasInitializedRef = useRef<boolean>(false);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isOverTriggerRef = useRef<boolean>(false);
    const isOverTooltipRef = useRef<boolean>(false);

    // Show tooltip immediately
    const handleShow = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        setIsVisible(true);
    }, []);

    // Hide tooltip with a small delay to allow moving to tooltip content
    const handleHide = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = globalThis.setTimeout(() => {
            if (!isOverTriggerRef.current && !isOverTooltipRef.current) {
                setIsVisible(false);
            }
        }, 100); // 100ms delay
    }, []);

    useEffect(() => {
        if (!isVisible) {
            hasInitializedRef.current = false;
            return;
        }

        const update = () => {
            const el = triggerRef.current;
            const tooltip = tooltipRef.current;
            if (!el) return;

            const rect = el.getBoundingClientRect();

            // compute responsive tooltip width: at most `maxWidth`, but fit within viewport with side margin
            const sideMargin = 12; // px
            const availableWidth = Math.max(80, window.innerWidth - sideMargin * 2);
            const width = Math.min(maxWidth, availableWidth);

            // Get actual tooltip height if available, otherwise estimate
            const tooltipHeight = tooltip
                ? tooltip.offsetHeight
                : Math.min(400, Math.max(50, Math.ceil(message.length / (width / 8)) * 20 + 30));

            // Calculate available space above and below in the visible viewport
            const spaceAbove = rect.top;
            const spaceBelow = window.innerHeight - rect.bottom;
            const minSpaceRequired = tooltipHeight + offset + 10; // 10px extra buffer
            const verticalMargin = 20; // px margin from viewport edges

            // Determine placement and position
            const hasSpaceAbove = spaceAbove >= minSpaceRequired;
            const hasSpaceBelow = spaceBelow >= minSpaceRequired;
            const shouldUseTop = hasSpaceAbove || (!hasSpaceBelow && spaceAbove > spaceBelow);

            const bestPlacement: 'top' | 'bottom' = shouldUseTop ? 'top' : 'bottom';
            let top: number;
            let calculatedMaxHeight: number;

            if (shouldUseTop) {
                if (hasSpaceAbove) {
                    // Normal top placement - enough space
                    top = rect.top - offset + window.scrollY;
                    calculatedMaxHeight = Math.max(100, spaceAbove - offset - verticalMargin);
                } else {
                    // Constrained top placement - not enough space but better than bottom
                    const constrainedTop = Math.max(sideMargin + tooltipHeight, rect.top - offset);
                    top = constrainedTop + window.scrollY;
                    calculatedMaxHeight = Math.max(100, spaceAbove - offset - verticalMargin);
                }
            } else {
                // Bottom placement - constrain maxHeight to available space
                top = rect.bottom + offset + window.scrollY;
                // Calculate how much space is available from the tooltip position to the bottom of viewport
                const availableSpaceBelow = window.innerHeight - rect.bottom - offset - verticalMargin;
                calculatedMaxHeight = Math.max(100, Math.min(600, availableSpaceBelow));
            }

            // Position tooltip centered above the icon
            const iconCenterX = rect.left + rect.width / 2;
            let left = iconCenterX - width / 2 + window.scrollX;

            // Constrain horizontally to stay within viewport
            // Adjust if tooltip would overflow
            if (left + width > window.innerWidth - sideMargin) {
                left = window.innerWidth - width - sideMargin + window.scrollX;
            }
            if (left - window.scrollX < sideMargin) {
                left = sideMargin + window.scrollX;
            }

            // Batch all state updates together to prevent flickering
            setPlacement(bestPlacement);
            setTooltipWidth(width);
            setTooltipMaxHeight(calculatedMaxHeight);
            setCoords({ left, top });

            hasInitializedRef.current = true;
        };

        // Throttled scroll handler using requestAnimationFrame
        const handleScroll = () => {
            if (scrollRafRef.current) {
                cancelAnimationFrame(scrollRafRef.current);
            }
            scrollRafRef.current = requestAnimationFrame(update);
        };

        // Clear any pending updates
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
            updateTimeoutRef.current = null;
        }

        // Initial update
        update();

        // Schedule a second update only if tooltip ref isn't available yet (for height measurement)
        if (!tooltipRef.current) {
            updateTimeoutRef.current = globalThis.setTimeout(update, 15);
        }

        window.addEventListener('resize', update);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
            if (scrollRafRef.current) {
                cancelAnimationFrame(scrollRafRef.current);
            }
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isVisible, offset, maxWidth, message]);

    const renderTooltip = () => {
        if (!isVisible) return null;

        const style: React.CSSProperties = {
            position: 'absolute',
            left: coords.left,
            top: coords.top,
            transform: placement === 'top' ? 'translateY(-100%)' : 'translateY(0)',
            width: tooltipWidth,
            maxHeight: tooltipMaxHeight,
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 9999,
            background: '#fff',
            color: '#222',
            border: '1px solid rgba(0,0,0,0.12)',
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            padding: '10px 12px',
            borderRadius: 8,
            whiteSpace: 'normal',
            lineHeight: 1.4,
            fontSize: 13,
            // Hide tooltip until position is calculated
            opacity: hasInitializedRef.current ? 1 : 0,
            transition: 'opacity 0.1s ease-in-out',
            // Scrollbar styling
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e0 #f7fafc',
        };

        // Add webkit scrollbar styles via CSS
        const scrollbarStyles = `
            .tooltip-scrollable::-webkit-scrollbar {
                width: 8px;
            }
            .tooltip-scrollable::-webkit-scrollbar-track {
                background: #f7fafc;
                border-radius: 4px;
            }
            .tooltip-scrollable::-webkit-scrollbar-thumb {
                background: #cbd5e0;
                border-radius: 4px;
            }
            .tooltip-scrollable::-webkit-scrollbar-thumb:hover {
                background: #a0aec0;
            }
        `;

        // moved out helper is used below

        const node = (
            <>
                <style>{scrollbarStyles}</style>
                <div
                    ref={tooltipRef}
                    role="tooltip"
                    id={id}
                    className="tooltip-scrollable"
                    style={style}
                    aria-hidden={!isVisible}
                    onMouseEnter={() => {
                        isOverTooltipRef.current = true;
                        if (hideTimeoutRef.current) {
                            clearTimeout(hideTimeoutRef.current);
                            hideTimeoutRef.current = null;
                        }
                    }}
                    onMouseLeave={() => {
                        isOverTooltipRef.current = false;
                        handleHide();
                    }}
                >
                    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{renderTooltipContent(message)}</div>
                </div>
            </>
        );

        return ReactDOM.createPortal(node, document.body);
    };

    // Helper: parse tooltip text into React nodes (paragraphs and lists)
    const renderTooltipContent = (text?: string) => {
        if (!text) return null;
        // normalize CRLF to LF
        const normalized = text.split('\r\n').join('\n').trim();
        if (!normalized) return null;

        const paragraphs = normalized.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);

        return (
            <>
                {paragraphs.map((para) => {
                    const lines = para.split('\n').map(l => l.trim()).filter(Boolean);
                    const isBullet = (line: string) => (/^[\u2022*-]\s+/.test(line));
                    const allBullets = lines.length > 0 && lines.every(isBullet);

                    if (allBullets) {
                        return (
                            <ul key={para} style={{ margin: '6px 0', paddingLeft: 20, listStyleType: 'disc' }}>
                                {lines.map((line) => (
                                    <li key={line}>{line.replace(/^[\u2022*-]\s+/, '')}</li>
                                ))}
                            </ul>
                        );
                    }

                    if (lines.length > 1) {
                        return (
                            <p key={para} style={{ margin: 0 }}>
                                {lines.map((line, i) => (
                                    <React.Fragment key={`${para}-${i}`}>
                                        {line}
                                        {i < lines.length - 1 && <br />}
                                    </React.Fragment>
                                ))}
                            </p>
                        );
                    }

                    return (
                        <p key={para} style={{ margin: 0 }}>
                            {lines[0]}
                        </p>
                    );
                })}
            </>
        );
    };

    return (
        <div className="inline-block">
            <button
                type="button"
                ref={triggerRef}
                aria-describedby={isVisible && id ? id : undefined}
                onMouseEnter={() => {
                    isOverTriggerRef.current = true;
                    handleShow();
                }}
                onMouseLeave={() => {
                    isOverTriggerRef.current = false;
                    handleHide();
                }}
                onFocus={() => {
                    isOverTriggerRef.current = true;
                    handleShow();
                }}
                onBlur={() => {
                    isOverTriggerRef.current = false;
                    handleHide();
                }}
                className="inline-flex items-center justify-center rounded-full text-[11px] font-medium hover:bg-gray-100 hover:text-black transition-colors bg-white mb-2"
                aria-label="Show help"
            >
                {children}
            </button>
            {renderTooltip()}
        </div>
    );
};

export default Tooltip;